import { Box, Link as ChakraLink, HStack } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useColorModeValue } from '~/color-mode'
import { findActivePillar, type NavPillar, navConfig } from './navConfig'

type PrimaryNavProps = { currentPath: string }

type PillarLinkProps = {
  pillar: NavPillar
  active: boolean
  open: boolean
  onOpen: () => void
  onClose: () => void
}

// The pillar label is always a real, navigable Link (so it carries
// `aria-current` and satisfies `getByRole('link', ...)`), not a button. The
// flyout for pillars with `children` is attached to that same link via a
// hover/focus-controlled sibling menu, rather than swapping the pillar itself
// for a menu-trigger button.
function PillarLink({ pillar, active, open, onOpen, onClose }: PillarLinkProps) {
  // Light mode: dark bar → white text, yellow active underline.
  // Dark mode: yellow bar → dark text, dark active underline (a yellow
  // underline on a yellow bar would be invisible).
  const inactiveColor = useColorModeValue('whiteAlpha.900', 'gray.900')
  const activeColor = useColorModeValue('white', 'gray.900')
  const activeBorder = useColorModeValue('brand.solid', 'gray.900')
  // Faint underline that animates in when hovering a non-active pillar.
  const hoverBorder = useColorModeValue('whiteAlpha.500', 'blackAlpha.500')
  return (
    <Box
      position="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      // Close only when focus leaves the whole pillar + flyout, so keyboard
      // users can Tab from the pillar into its flyout children without it
      // collapsing under them.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          onClose()
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }}
    >
      <ChakraLink
        asChild
        aria-current={active ? 'page' : undefined}
        aria-haspopup={pillar.children ? 'menu' : undefined}
        aria-expanded={pillar.children ? open : undefined}
        display="inline-block"
        px={8}
        py={4}
        fontWeight={active ? 'bold' : 'medium'}
        color={active ? activeColor : inactiveColor}
        borderBottomWidth="3px"
        borderColor={active ? activeBorder : 'transparent'}
        transition="color 0.2s ease, border-color 0.2s ease, transform 0.15s ease"
        outline="none"
        _hover={
          active
            ? { textDecoration: 'none' }
            : {
                textDecoration: 'none',
                color: activeColor,
                borderColor: hoverBorder,
                transform: 'translateY(-1px)',
              }
        }
        // Suppress the focus rectangle on mouse click; keep an accessible
        // ring for keyboard navigation only.
        _focus={{ outline: 'none', boxShadow: 'none' }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: activeBorder,
          outlineOffset: '-4px',
          borderRadius: 'sm',
        }}
        onFocus={onOpen}
      >
        <Link to={pillar.to as string}>{pillar.label}</Link>
      </ChakraLink>
      {pillar.children && open && (
        <Box
          className="nav-flyout"
          position="absolute"
          top="100%"
          left={0}
          zIndex={20}
          minW="220px"
          p={1.5}
          mt={2}
          bg="bg.panel"
          borderWidth="1px"
          borderColor="border"
          borderRadius="xl"
          boxShadow="xl"
        >
          {pillar.children.map((child) => {
            const ChildIcon = child.icon
            return (
              <ChakraLink
                key={child.to}
                asChild
                display="flex"
                alignItems="center"
                gap={3}
                px={3}
                py={2.5}
                borderRadius="lg"
                fontSize="sm"
                fontWeight="medium"
                color="content.secondary"
                transition="background-color 0.15s ease, color 0.15s ease, transform 0.15s ease"
                _hover={{
                  bg: 'bg.subtle',
                  color: 'content.primary',
                  textDecoration: 'none',
                  transform: 'translateX(2px)',
                  '& svg': { color: 'brand.solid' },
                }}
              >
                <Link to={child.to as string}>
                  {ChildIcon ? (
                    <ChildIcon size={18} style={{ transition: 'color 0.15s ease' }} />
                  ) : null}
                  <span>{child.label}</span>
                </Link>
              </ChakraLink>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export function PrimaryNav({ currentPath }: PrimaryNavProps) {
  const activePillar = findActivePillar(currentPath)
  const [openId, setOpenId] = useState<string | undefined>(undefined)
  // Dark bar in light mode, yellow (brand) bar in dark mode.
  const navBg = useColorModeValue('gray.900', 'brand.solid')

  return (
    <Box as="nav" bg={navBg} w="100%">
      <HStack justify="center" gap={0} maxW="1400px" mx="auto">
        {navConfig.map((pillar) => (
          <PillarLink
            key={pillar.id}
            pillar={pillar}
            active={activePillar?.id === pillar.id}
            open={openId === pillar.id}
            onOpen={() => setOpenId(pillar.id)}
            onClose={() => setOpenId((current) => (current === pillar.id ? undefined : current))}
          />
        ))}
      </HStack>
    </Box>
  )
}
